import { ReactElement, useEffect, useMemo, useState } from "react";
import { Dropdown, Field, Hint, Item, Menu, Multiselect } from "@zendeskgarden/react-dropdowns";
import { Tag } from "@zendeskgarden/react-tags";
import Downshift from "downshift";
import { FinancialEntity } from "../../../contexts/chartofaccounts/ChartOfAccountsContext";
import {COLORS} from "../../../constants/Colors";

export interface IAccountsSelectorProps {
    allAccounts: FinancialEntity[],
    selectedAccountIds: string[],
    onChangeCallback: (accountIds: string[]) => void,
    // the string returned from the validation will display beneath the field,
    // and returning any value other than undefined will also apply 'error' styling to the dropdown
    onValidate?: (selectedItems: string[]) => string | undefined;
}

function orderBy(unordered: string[], accounts: FinancialEntity[]): string[] {
    const pre = accounts.filter(acc => unordered.includes(acc.id)).sortBy("order");
    return pre.map(acc => acc.id);
}

function nameOf(fe: FinancialEntity | undefined): string {
    if (!fe) {
        return "";
    }
    let name = fe.name;
    if (fe.number) {
        name = name + ` (${fe.number})`;
    }
    return name;
}

export default function AccountsSelector(props: IAccountsSelectorProps): ReactElement {
    const [inputValue, setInputValue] = useState('');
    const [matchingOptions, setMatchingOptions] = useState<FinancialEntity[]>(props.allAccounts);
    const initialSelectedAccounts = useMemo(() => orderBy(props.selectedAccountIds, props.allAccounts), []);
    const [selectedAccounts, setSelectedAccounts] = useState<string[]>(initialSelectedAccounts);
    let requestDebounce: (ReturnType<typeof setTimeout> | null) = null;

    const stateReducer = (state: any, changes: any) => {
        switch (changes.type) {
            case Downshift.stateChangeTypes.keyDownEnter:
            case Downshift.stateChangeTypes.clickItem:
                return {
                    ...changes,
                    highlightedIndex: state.highlightedIndex,
                    isOpen: true,
                    inputValue: '',
                };
            default:
                return changes;
        }
    };

    useEffect(
        () => {
            const matchedOptions = props.allAccounts.filter(option => {
                return nameOf(option).trim().toLowerCase().indexOf(inputValue.trim().toLowerCase()) !== -1;
            });

            setMatchingOptions(matchedOptions);
        }, [inputValue]
    );

    function handleSelect(selectedAccountIds: string[]) {
        setSelectedAccounts(orderBy(selectedAccountIds, props.allAccounts));
        if (requestDebounce) {
            clearTimeout(requestDebounce);
        }

        requestDebounce = setTimeout(() => {
            props.onChangeCallback(selectedAccountIds);
        }, 1000);
    }

    const renderOptions = () => {
        if (inputValue == null || inputValue == '') {
            return props.allAccounts.map(option => (
                <Item key={option.id} value={option.id}>
                    <span>{nameOf(option)}</span>
                </Item>
            ));
        }

        if (matchingOptions.length === 0) {
            return <Item disabled>No account found</Item>;
        }

        return matchingOptions.map(option => (
            <Item key={option.id} value={option.id}>
                <span>{nameOf(option)}</span>
            </Item>
        ));
    };

    return (
        <Dropdown
            inputValue={inputValue}
            selectedItems={selectedAccounts}
            onSelect={items => {
                handleSelect(items);
            }}
            downshiftProps={{ defaultHighlightedIndex: 0, stateReducer }}
            onInputValueChange={value => setInputValue(value)}
        >
            <Field>
                <Multiselect
                    validation={props.onValidate?.(selectedAccounts) !== undefined ? "error" : undefined}
                    isCompact
                    placeholder={"Select Accounts"}
                    renderItem={({ value, removeValue }) => (
                        <Tag>
                            <span>{nameOf(props.allAccounts.find(x => x.id == value))}</span>
                            <Tag.Close onClick={removeValue}
                            />
                        </Tag>
                    )}
                />
                {props.onValidate &&
                    <Hint style={{color: COLORS.RED_500}}>{props.onValidate(selectedAccounts)}</Hint>
                }
            </Field>
            <Menu placement="start">
                {renderOptions()}
            </Menu>
        </Dropdown>
    );
}